import type { CheerioAPI } from "cheerio";
import type { Url, AdapterId } from "../data-model/primitives.js";
import type { NovelMetadata, ChapterRef, Chapter } from "../data-model/novel.js";
import type { AdapterContext as FwdAdapterContext } from "../data-model/fwd.js";
import type { BrowserClient } from "../http/browser/types.js";
import { DownloadError, ParseError } from "../core/errors.js";
import { parseUrl } from "../data-model/parse.js";
import { loadHtml } from "../utils/html.js";
import { matchesAny } from "../utils/url.js";

/**
 * Runtime services handed to every adapter method: the {@link HttpClient}, a
 * logger, the abort signal, an optional browser client (present only for
 * browser-tier adapters), and — during chapter fetches — the already-fetched
 * novel metadata.
 */
export interface AdapterContext extends FwdAdapterContext {
  readonly browser?: BrowserClient;
}

/**
 * The contract every site adapter implements. Register custom adapters via
 * `createDownloader({ adapters })` or the `Downloader` constructor. Extending
 * {@link BaseAdapter} covers `canHandle` and the HTML/JSON helpers for you.
 * See `docs/adapter-spec.md`.
 */
export interface SiteAdapter {
  /** Stable adapter id, also stamped onto `NovelMetadata.sourceSite`. */
  readonly id: AdapterId;
  /** Human-readable site name. */
  readonly displayName: string;
  /** Hostnames this adapter claims (used by the default `canHandle`). */
  readonly hostnames: readonly string[];
  /**
   * Optional transport hint. Adapters that require a real browser (e.g. for
   * XHR-rendered TOCs or Cloudflare-protected pages) set this to `"browser"`.
   * Omitting the field (or setting it to `"http"`) selects the default HTTP
   * transport.
   */
  readonly preferredTransport?: "http" | "browser";
  /** Whether this adapter handles `url` (typically a hostname match). */
  canHandle(url: string): boolean;
  /** Fetch novel-level metadata. Throw a {@link DownloadError} subclass on failure. */
  fetchMetadata(url: Url, ctx: AdapterContext): Promise<NovelMetadata>;
  /**
   * Yield chapter references for the whole table of contents. Refs MUST be
   * yielded in ascending `index` order — the pipeline short-circuits a
   * `chapterRange` once it passes the upper bound and relies on this ordering.
   */
  fetchChapterList(url: Url, ctx: AdapterContext): AsyncIterable<ChapterRef>;
  /** Fetch and normalize one chapter body. */
  fetchChapter(ref: ChapterRef, ctx: AdapterContext): Promise<Chapter>;
}

/**
 * Convenience base for adapters: implements `canHandle` (hostname match) and
 * provides protected `loadHtml` / `fetchJson` helpers. Extend it and implement
 * the three `fetch*` methods.
 */
export abstract class BaseAdapter implements SiteAdapter {
  abstract readonly id: AdapterId;
  abstract readonly displayName: string;
  abstract readonly hostnames: readonly string[];
  readonly preferredTransport?: "http" | "browser";
  abstract fetchMetadata(url: Url, ctx: AdapterContext): Promise<NovelMetadata>;
  abstract fetchChapterList(url: Url, ctx: AdapterContext): AsyncIterable<ChapterRef>;
  abstract fetchChapter(ref: ChapterRef, ctx: AdapterContext): Promise<Chapter>;

  canHandle(url: string): boolean {
    try {
      return matchesAny(parseUrl(url), this.hostnames);
    } catch {
      return false;
    }
  }

  protected async loadHtml(url: Url, ctx: AdapterContext): Promise<CheerioAPI> {
    try {
      const res = await ctx.http.get(url, { signal: ctx.signal });
      return loadHtml(res.body);
    } catch (cause) {
      // Pass DownloadError subclasses through; wrap others.
      if (cause instanceof DownloadError) throw cause;
      throw new ParseError(`Failed to load ${url}`, { cause, url });
    }
  }

  protected async fetchJson(url: Url, ctx: AdapterContext): Promise<unknown> {
    let res: Awaited<ReturnType<typeof ctx.http.get>>;
    try {
      res = await ctx.http.get(url, { signal: ctx.signal });
    } catch (cause) {
      if (cause instanceof DownloadError) throw cause;
      throw new ParseError(`Failed to fetch JSON at ${url}`, { cause, url });
    }
    try {
      return JSON.parse(res.body);
    } catch (cause) {
      throw new ParseError(`Invalid JSON at ${url}`, { cause, url });
    }
  }
}
